import React from 'react';
import ElementListRow from '@features/proposals/ElementListRow/ElementListRow';
import {
  ProjectChangeType,
  type ProjectChange,
} from '@shared/helpers/pipeline/update-engine.models';
import type {
  Baseline,
  ProjectMarketEstimates,
} from '@shared/models/project.model';
import {
  getChangeTypeLabel,
  getChangeValueLabel,
  resolveKnownPropertyDisplayName,
} from '@shared/helpers/actions/action.helpers';
import {
  getCarbonProductOverrideUiSlice,
  getTransportOrSpillOverrideUiSlice,
} from '@shared/helpers/actions/product-element-override-display.helpers';
import {
  FORMULA_UPDATED_CHANGE_DISPLAY_VALUE,
  countChangePayloadShowsNumericUnit,
  formatChangeNumericDisplayString,
  formatElementPropertyCountChangeDisplay,
  formatPropertyFormattedDisplayValue,
  formatQuantityFormattedDisplayValue,
} from '@shared/helpers/actions/change-value-display.helpers';
import {
  elementQuantityUnitRecord,
  elementQuantityDisplayNameRecord,
} from '@shared/models/element-quantities.model';
import { getElementById } from '@shared/helpers/recursive-element.helpers';
import type { ElementQuantityName } from '@shared/models/element-quantities.model';

const CHANGE_NAME_TEXT_STYLE = {
  color: 'rgba(0,0,0,0.6)',
  WebkitTextFillColor: 'rgba(0,0,0,0.6)',
} as const;

/** Looks up the displayName of a property or quantity on the element, falling back to known maps then the raw name. */
const resolveDisplayName = (
  rawName: string,
  elementId: string,
  version: Baseline,
  kind: 'property' | 'quantity',
): string => {
  const element = getElementById(version, elementId, false);
  if (!element) {
    return kind === 'quantity'
      ? (elementQuantityDisplayNameRecord[rawName as ElementQuantityName] ??
          rawName)
      : resolveKnownPropertyDisplayName(rawName);
  }
  const items =
    kind === 'property'
      ? (
          element as {
            properties?: Array<{ name: string; displayName?: string }>;
          }
        ).properties
      : Object.values(
          (
            element as {
              quantity?: Record<string, { name: string; displayName?: string }>;
            }
          ).quantity ?? {},
        );
  const match = items?.find((p) => p.name === rawName);
  return (
    match?.displayName ??
    (kind === 'quantity'
      ? (elementQuantityDisplayNameRecord[rawName as ElementQuantityName] ??
        rawName)
      : resolveKnownPropertyDisplayName(rawName))
  );
};

/**
 * Extracts the human-readable name of what property/quantity a change targets.
 * Prefers displayName from the element definition over the raw technical name.
 */
const getChangeName = (
  change: ProjectChange,
  version: Baseline,
  marketEstimates?: ProjectMarketEstimates,
): string => {
  switch (change.type) {
    case ProjectChangeType.ElementPropertyValue:
    case ProjectChangeType.ElementPropertyResolved:
    case ProjectChangeType.ElementPropertyExpression:
    case ProjectChangeType.ElementPropertyFormatted:
    case ProjectChangeType.ElementPropertyFallbackCount:
    case ProjectChangeType.ElementPropertyCount:
      return resolveDisplayName(
        change.propertyName,
        change.elementId,
        version,
        'property',
      );
    case ProjectChangeType.ElementQuantityResolved:
    case ProjectChangeType.ElementQuantityExpression:
    case ProjectChangeType.ElementQuantityFallbackExpression:
    case ProjectChangeType.ElementQuantityFallbackCount:
    case ProjectChangeType.ElementQuantityFormatted:
    case ProjectChangeType.ElementQuantityValue:
    case ProjectChangeType.ElementQuantityCount:
      return resolveDisplayName(
        change.quantityName,
        change.elementId,
        version,
        'quantity',
      );
    case ProjectChangeType.ElementQuantitySelect:
      return change.quantityName
        ? resolveDisplayName(
            change.quantityName,
            change.elementId,
            version,
            'quantity',
          )
        : getChangeTypeLabel(change.type);
    case ProjectChangeType.ProductElementCarbonLevelOverride:
    case ProjectChangeType.ProductElementTransportOverride:
    case ProjectChangeType.ProductElementWasteOverride: {
      const slice =
        getCarbonProductOverrideUiSlice(change, version, marketEstimates) ??
        getTransportOrSpillOverrideUiSlice(change);
      if (slice !== undefined) return slice.rowTitle;
      return getChangeTypeLabel(change.type);
    }
    default:
      return getChangeTypeLabel(change.type);
  }
};

/**
 * Extracts just the value part of a change — without the "name = " prefix that
 * {@link getChangeValueLabel} includes for property changes.
 */
const getChangeValue = (
  change: ProjectChange,
  version?: Baseline,
  marketEstimates?: ProjectMarketEstimates,
): string | undefined => {
  switch (change.type) {
    case ProjectChangeType.ElementPropertyValue:
      return change.overrideValue !== undefined
        ? formatChangeNumericDisplayString({
            kind: 'property',
            name: change.propertyName,
            value: change.overrideValue,
            elementId: change.elementId,
            version,
          })
        : undefined;
    case ProjectChangeType.ElementPropertyResolved:
      return formatChangeNumericDisplayString({
        kind: 'property',
        name: change.propertyName,
        value: change.value,
        elementId: change.elementId,
        version,
      });
    case ProjectChangeType.ElementPropertyExpression:
      return FORMULA_UPDATED_CHANGE_DISPLAY_VALUE;
    case ProjectChangeType.ElementPropertyFormatted:
      return formatPropertyFormattedDisplayValue(
        change.formatted,
        change.propertyName,
        change.elementId,
        version,
      );
    case ProjectChangeType.ElementPropertyFallbackCount: {
      const valueStr = formatElementPropertyCountChangeDisplay({
        propertyName: change.propertyName,
        elementId: change.elementId,
        version,
        payload: change.fallbackCount,
      });
      return valueStr.length > 0 ? valueStr : '(fallback)';
    }
    case ProjectChangeType.ElementPropertyCount: {
      const valueStr = formatElementPropertyCountChangeDisplay({
        propertyName: change.propertyName,
        elementId: change.elementId,
        version,
        payload: change.count,
      });
      return valueStr.length > 0 ? valueStr : '(count)';
    }
    case ProjectChangeType.ElementQuantityResolved:
      return formatChangeNumericDisplayString({
        kind: 'quantity',
        name: change.quantityName,
        value: change.value,
        elementId: change.elementId,
        version,
      });
    case ProjectChangeType.ElementQuantityExpression:
    case ProjectChangeType.ElementQuantityFallbackExpression:
      return FORMULA_UPDATED_CHANGE_DISPLAY_VALUE;
    case ProjectChangeType.ElementQuantityCount: {
      if (change.count === undefined) return undefined;
      const valueStr = formatElementPropertyCountChangeDisplay({
        propertyName: change.quantityName,
        elementId: change.elementId,
        version,
        payload: change.count,
      });
      return valueStr.length > 0 ? valueStr : undefined;
    }
    case ProjectChangeType.ElementQuantityFallbackCount: {
      const valueStr = formatElementPropertyCountChangeDisplay({
        propertyName: change.quantityName,
        elementId: change.elementId,
        version,
        payload: change.fallbackCount,
      });
      return valueStr.length > 0 ? valueStr : undefined;
    }
    case ProjectChangeType.ElementQuantityFormatted:
      return formatQuantityFormattedDisplayValue(
        change.formatted,
        change.quantityName,
        change.elementId,
        version,
      );
    case ProjectChangeType.ElementQuantityValue:
      return change.overrideValue !== undefined
        ? formatChangeNumericDisplayString({
            kind: 'quantity',
            name: change.quantityName,
            value: change.overrideValue,
            elementId: change.elementId,
            version,
          })
        : undefined;
    case ProjectChangeType.ProductElementCarbonLevelOverride:
    case ProjectChangeType.ProductElementTransportOverride:
    case ProjectChangeType.ProductElementWasteOverride: {
      const slice =
        getCarbonProductOverrideUiSlice(change, version, marketEstimates) ??
        getTransportOrSpillOverrideUiSlice(change);
      if (slice !== undefined) return slice.rowValue;
      return getChangeValueLabel(change, version, { marketEstimates });
    }
    default:
      return getChangeValueLabel(change, version, { marketEstimates });
  }
};

const quantityUnitForName = (
  quantityName: ElementQuantityName,
): string | undefined => {
  const u =
    elementQuantityUnitRecord[
      quantityName as keyof typeof elementQuantityUnitRecord
    ];
  if (u === '%') return undefined;
  return u;
};

const getChangeUnit = (
  change: ProjectChange,
  marketEstimates?: ProjectMarketEstimates,
): string | undefined => {
  switch (change.type) {
    case ProjectChangeType.ElementQuantityResolved:
    case ProjectChangeType.ElementQuantityValue:
    case ProjectChangeType.ElementQuantityFormatted:
      return quantityUnitForName(change.quantityName);
    case ProjectChangeType.ElementQuantityCount:
      if (change.count === undefined) return undefined;
      return countChangePayloadShowsNumericUnit(change.count)
        ? quantityUnitForName(change.quantityName)
        : undefined;
    case ProjectChangeType.ElementQuantityFallbackCount:
      return countChangePayloadShowsNumericUnit(change.fallbackCount)
        ? quantityUnitForName(change.quantityName)
        : undefined;
    case ProjectChangeType.ProductElementCarbonLevelOverride:
    case ProjectChangeType.ProductElementTransportOverride:
    case ProjectChangeType.ProductElementWasteOverride:
      return (
        getCarbonProductOverrideUiSlice(change, undefined, marketEstimates) ??
        getTransportOrSpillOverrideUiSlice(change)
      )?.rowUnit;
    default:
      return undefined;
  }
};

type ActionChangeRowProps = {
  change: ProjectChange;
  version: Baseline;
  /** Project market snapshot; improves carbon percentile → % display vs defaults-only fallback */
  marketEstimates?: ProjectMarketEstimates;
};

export const ActionChangeRow: React.FC<ActionChangeRowProps> = ({
  change,
  version,
  marketEstimates,
}) => {
  const name = getChangeName(change, version, marketEstimates);
  const value = getChangeValue(change, version, marketEstimates);
  const unit = getChangeUnit(change, marketEstimates);

  return (
    <ElementListRow
      name={name}
      nameReadonly
      nameDisplayStyle={CHANGE_NAME_TEXT_STYLE}
      quantityValue={value}
      quantityReadonly
      unit={unit}
      resizable={false}
      hideSurfaceBackground
      leftGap={8}
      depth={1}
      height={24}
      iconSlot={null}
      prefixIconSlots={[null]}
    />
  );
};
